<template>
  <div class="products-benefits-section">
    <div class="benefits-container">
      <div class="content-header">
        <div class="section-badge">
          <div class="badge-line"></div>
          <span class="badge-text">BENEFICIOS PRIMER AÑO</span>
        </div>
        <h2 class="benefits-title">Beneficios que obtendrás en tu primer año</h2>
        <p class="benefits-subtitle">
          Datos reales de una flota de transporte con 250 vehículos que implementó FourinTrack
        </p>
      </div>

      <div class="benefits-grid">
        <div
          v-for="(benefit, index) in benefits"
          :key="index"
          ref="cardRefs"
          class="benefit-card"
          :class="{ expanded: expandedIndex === index }"
          @click="toggleExpand(index)"
          :aria-expanded="expandedIndex === index"
        >
          <!-- Contenido colapsado -->
          <div class="card-header">
            <div class="benefit-icon">
              <span class="material-symbols-outlined">{{ benefit.icon }}</span>
            </div>
            <h3 class="benefit-title">{{ benefit.title }}</h3>
            <p class="benefit-description">{{ benefit.shortDesc }}</p>
            <div class="expand-indicator">
              <span class="material-symbols-outlined">
                {{ expandedIndex === index ? 'expand_less' : 'expand_more' }}
              </span>
            </div>
          </div>

          <!-- Contenido expandido -->
          <div class="card-expanded-content" v-if="expandedIndex === index">
            <div class="expanded-inner">
              <!-- Número destacado -->
              <div class="highlight-number">
                <span class="number-value">{{ benefit.percentage }}</span>
                <span class="number-label">{{ benefit.percentageLabel }}</span>
              </div>

              <!-- Gráfica comparativa -->
              <div class="chart-container" v-if="benefit.chartData">
                <div class="chart-title">Comparativa antes / después</div>
                <div class="chart-bars">
                  <div class="chart-bar-wrapper">
                    <div class="chart-label">Antes</div>
                    <div class="chart-bar-wrapper-inner">
                      <div class="chart-bar-container">
                        <div
                          class="chart-bar before-bar"
                          :style="{ width: benefit.chartData.before + '%' }"
                        >
                          <div class="chart-tooltip">
                            <span class="tooltip-value">{{ benefit.chartData.beforeLabel }}</span>
                            <span class="tooltip-label">Antes</span>
                          </div>
                        </div>
                        <span class="chart-value">{{ benefit.chartData.beforeLabel }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="chart-bar-wrapper">
                    <div class="chart-label">Después</div>
                    <div class="chart-bar-wrapper-inner">
                      <div class="chart-bar-container">
                        <div
                          class="chart-bar after-bar"
                          :style="{ width: benefit.chartData.after + '%' }"
                        >
                          <div class="chart-tooltip">
                            <span class="tooltip-value">{{ benefit.chartData.afterLabel }}</span>
                            <span class="tooltip-label">Después</span>
                          </div>
                        </div>
                        <span class="chart-value">{{ benefit.chartData.afterLabel }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Caso real -->
              <div class="case-data" v-if="benefit.caseData">
                <div class="case-title">
                  <span class="material-symbols-outlined">business</span>
                  Caso real: Flota de 250 vehículos
                </div>
                <div class="case-details">
                  <div class="case-item" v-if="benefit.caseData.before">
                    <span class="case-label">Antes:</span>
                    <span class="case-value">{{ benefit.caseData.before }}</span>
                  </div>
                  <div class="case-item" v-if="benefit.caseData.after">
                    <span class="case-label">Después:</span>
                    <span class="case-value highlight">{{ benefit.caseData.after }}</span>
                  </div>
                  <div class="case-item savings" v-if="benefit.caseData.savings">
                    <span class="case-label">Ahorro:</span>
                    <span class="case-value savings-value">{{ benefit.caseData.savings }}</span>
                  </div>
                  <div class="case-item" v-if="benefit.caseData.additional">
                    <span class="case-value additional">{{ benefit.caseData.additional }}</span>
                  </div>
                </div>
              </div>

              <!-- Métricas generales -->
              <div class="metrics-section" v-if="benefit.metrics">
                <div class="metrics-title">
                  <span class="material-symbols-outlined">insights</span>
                  Métricas generales esperadas
                </div>
                <p class="metrics-text">{{ benefit.metrics }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue'

const expandedIndex = ref<number | null>(null)
const cardRefs = ref<(HTMLElement | null)[]>([])

interface BenefitData {
  title: string
  icon: string
  shortDesc: string
  percentage: string
  percentageLabel?: string
  chartData?: {
    before: number
    after: number
    beforeLabel: string
    afterLabel: string
  }
  caseData?: {
    before?: string
    after?: string
    savings?: string
    additional?: string
  }
  metrics?: string
}

const benefits: BenefitData[] = [
  {
    title: 'Reducción de costos',
    icon: 'trending_down',
    shortDesc:
      'Reduce hasta un 30% los costos operativos mediante la optimización de recursos y prevención de fallas costosas.',
    percentage: '30%',
    percentageLabel: 'Reducción de costos',
    chartData: {
      before: 100,
      after: 70,
      beforeLabel: '3.0 M$',
      afterLabel: '2.1 M$',
    },
    caseData: {
      before: '3.0 M$',
      after: '2.1 M$',
      savings: '1.8 M$ al año',
      additional: 'Ahorro por intervenir solo cuando es necesario',
    },
    metrics: '30% reducción típica en costos operativos de mantenimiento',
  },
  {
    title: 'Mejora en tiempos de respuesta',
    icon: 'speed',
    shortDesc:
      'Disminuye el MTTR (Mean Time To Repair) con alertas inteligentes y asignación eficiente de recursos.',
    percentage: '45%',
    percentageLabel: 'Menos incidentes',
    chartData: {
      before: 100,
      after: 55,
      beforeLabel: '100%',
      afterLabel: '55%',
    },
    caseData: {
      before: 'Alto número de incidentes',
      after: '45% menos incidentes de inactividad',
      additional: '60% menos reparaciones de emergencia',
    },
    metrics: 'Reducción significativa del MTTR con intervención proactiva',
  },
  {
    title: 'Mayor disponibilidad',
    icon: 'check_circle',
    shortDesc:
      'Aumenta la disponibilidad de activos mediante mantenimientos preventivos programados y predictivos.',
    percentage: '+5%',
    percentageLabel: 'Uptime',
    chartData: {
      before: 92,
      after: 97,
      beforeLabel: '92%',
      afterLabel: '97%',
    },
    caseData: {
      before: '92% disponibilidad',
      after: '97% disponibilidad',
      additional: 'Hasta 25% de mejora en tiempo operativo',
    },
    metrics: 'La gestión predictiva puede elevar el tiempo operativo hasta en un 25%',
  },
  {
    title: 'Cumplimiento de mantenimientos',
    icon: 'schedule',
    shortDesc:
      'Alcanza hasta un 95% de cumplimiento en mantenimientos preventivos con alertas automáticas y seguimiento en tiempo real.',
    percentage: '95%',
    percentageLabel: 'Cumplimiento',
    chartData: {
      before: 70,
      after: 95,
      beforeLabel: '70%',
      afterLabel: '95%',
    },
    caseData: {
      before: 'Cumplimiento variable',
      after: '95% en mantenimientos preventivos',
      additional: 'Notificaciones anticipadas a técnicos evitan omisiones',
    },
    metrics: 'La literatura recomienda alcanzar un cumplimiento cercano al 90% o más',
  },
  {
    title: 'Menos paradas no planificadas',
    icon: 'warning',
    shortDesc:
      'Reduce las paradas imprevistas mediante detección temprana de problemas y mantenimiento proactivo.',
    percentage: '45%',
    percentageLabel: 'Reducción',
    chartData: {
      before: 100,
      after: 55,
      beforeLabel: '100%',
      afterLabel: '55%',
    },
    caseData: {
      before: 'Alto número de paradas',
      after: '45% menos paradas no planificadas',
      additional: 'Detección temprana de fallas potenciales',
    },
    metrics: 'Reducciones típicas de 30-50% en tiempos de inactividad no planificado',
  },
  {
    title: 'Optimización de inventario',
    icon: 'inventory_2',
    shortDesc:
      'Reduce el inventario de repuestos mediante un mejor control y planificación de necesidades.',
    percentage: '25%',
    percentageLabel: 'Reducción',
    chartData: {
      before: 100,
      after: 75,
      beforeLabel: '100%',
      afterLabel: '75%',
    },
    caseData: {
      before: 'Stock elevado',
      after: '25% reducción en inventario',
      additional: 'Solicitud solo de lo necesario antes de cada intervención',
    },
    metrics: 'Reducción típica de 20-25% en inventario de repuestos',
  },
  {
    title: 'KPIs mejorados',
    icon: 'bar_chart',
    shortDesc:
      'Mejora significativamente tus indicadores: MTBF ↑, MTTR ↓, % cumplimiento OT ↑ y disponibilidad de activos.',
    percentage: '220%',
    percentageLabel: 'ROI',
    chartData: {
      before: 92,
      after: 97,
      beforeLabel: '92% uptime',
      afterLabel: '97% uptime',
    },
    caseData: {
      before: '92% uptime global',
      after: '97% uptime global',
      savings: '220% ROI en el primer año',
      additional: 'Mejora en todos los KPIs de mantenimiento',
    },
    metrics: 'Aumento significativo del uptime y rentabilidad sobre la inversión',
  },
  {
    title: 'Menos errores humanos',
    icon: 'error',
    shortDesc:
      'Minimiza errores con checklists estandarizados, validaciones automáticas y procesos documentados.',
    percentage: '60%',
    percentageLabel: 'Reducción',
    chartData: {
      before: 100,
      after: 40,
      beforeLabel: '100%',
      afterLabel: '40%',
    },
    caseData: {
      before: 'Alto número de errores',
      after: '60% menos reparaciones de emergencia',
      additional: 'Digitalización de procesos y checklists estandarizados',
    },
    metrics: 'Minimización de olvidos y errores mediante procesos digitalizados',
  },
  {
    title: 'Mejor planificación',
    icon: 'event_available',
    shortDesc:
      'Optimiza la programación de mantenimientos con herramientas de planificación avanzadas y asignación inteligente de recursos.',
    percentage: '—',
    percentageLabel: 'Cualitativo',
    caseData: {
      additional:
        'Programación proactiva en ventanas óptimas y asignación del personal adecuado. Centralización de información clarifica tareas y facilita comunicación entre equipos.',
    },
    metrics: 'Mejora significativa en planificación y coordinación de equipos',
  },

]

const toggleExpand = async (index: number) => {
  if (expandedIndex.value === index) {
    expandedIndex.value = null
  } else {
    expandedIndex.value = index
    // Esperar a que el DOM se actualice antes de hacer scroll
    await nextTick()
    // Hacer scroll suave hacia la tarjeta expandida
    const cardElement = cardRefs.value[index]
    if (cardElement) {
      cardElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      })
    }
  }
}
</script>

<style scoped>
.products-benefits-section {
  padding: 80px 20px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  min-height: 60vh;
}

.benefits-container {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.content-header {
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: center;
  margin-bottom: 60px;
}

.section-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.badge-line {
  width: 40px;
  height: 2px;
  background: #129bef;
}

.badge-text {
  color: #129bef;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.benefits-title {
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: 600;
  color: #333;
  line-height: 1.2;
  margin: 0;
}

.benefits-subtitle {
  font-size: 18px;
  line-height: 1.6;
  color: #666;
  margin: 0;
  max-width: 700px;
  margin: 0 auto;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  grid-auto-rows: min-content;
}

.benefit-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  grid-column: span 1;
}

.benefit-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #129bef 0%, #4ab7ff 100%);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.benefit-card:hover::before,
.benefit-card.expanded::before {
  transform: scaleX(1);
}

.benefit-card:hover:not(.expanded) {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
  border-color: rgba(18, 155, 239, 0.2);
}

.benefit-card.expanded {
  grid-column: span 3;
  box-shadow: 0 20px 50px rgba(18, 155, 239, 0.2);
  border-color: #129bef;
  z-index: 10;
}

.card-header {
  padding: 40px 30px;
  transition: padding 0.4s ease-in-out;
}

.benefit-card.expanded .card-header {
  padding-bottom: 20px;
}

.benefit-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #129bef 0%, #4ab7ff 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: white;
  font-size: 2rem;
  box-shadow: 0 8px 25px rgba(18, 155, 239, 0.3);
  transition: all 0.3s ease;
}

.benefit-card:hover .benefit-icon,
.benefit-card.expanded .benefit-icon {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 12px 30px rgba(18, 155, 239, 0.4);
}

.benefit-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
  line-height: 1.3;
}

.benefit-description {
  font-size: 15px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 16px 0;
}

.expand-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  color: #129bef;
  transition: transform 0.3s ease;
}

.benefit-card.expanded .expand-indicator {
  transform: rotate(180deg);
}

.expand-indicator .material-symbols-outlined {
  font-size: 32px;
}

/* Contenido expandido */
.card-expanded-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s ease-in-out, opacity 0.3s ease-in-out;
  opacity: 0;
}

.benefit-card.expanded .card-expanded-content {
  max-height: 2000px;
  opacity: 1;
}

.expanded-inner {
  padding: 0 30px 40px 30px;
  animation: fadeIn 0.5s ease-in-out;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  align-items: start;
}

@media (min-width: 1025px) {
  .expanded-inner {
    grid-template-columns: 1fr 1fr;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Número destacado */
.highlight-number {
  grid-column: 1 / -1;
  text-align: center;
  padding: 30px 0;
  border-top: 2px solid #e9ecef;
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 30px;
}

.number-value {
  display: block;
  font-size: 4rem;
  font-weight: 800;
  color: #129bef;
  line-height: 1;
  margin-bottom: 8px;
}

.number-label {
  display: block;
  font-size: 1.1rem;
  color: #666;
  font-weight: 600;
}

/* Gráfica */
.chart-container {
  margin-bottom: 30px;
  grid-column: span 1;
}

.chart-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
}

.chart-bars {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.chart-bar-wrapper {
  display: flex;
  align-items: center;
  gap: 15px;
}

.chart-bar-wrapper-inner {
  flex: 1;
  position: relative;
  overflow: visible;
}

.chart-label {
  min-width: 80px;
  font-size: 14px;
  font-weight: 600;
  color: #666;
}

.chart-bar-container {
  position: relative;
  height: 40px;
  background: #f0f0f0;
  border-radius: 8px;
}

.chart-bar {
  height: 100%;
  border-radius: 8px;
  transition: width 0.8s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 12px;
  position: relative;
  cursor: pointer;
}

.chart-bar:hover {
  filter: brightness(1.1);
}

.before-bar {
  background: #9ca3af;
}

.after-bar {
  background: linear-gradient(90deg, #129bef 0%, #4ab7ff 100%);
}

.chart-value {
  position: absolute;
  right: 12px;
  font-size: 14px;
  font-weight: 700;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  display: none;
}

.before-bar .chart-value {
  color: #333;
  text-shadow: none;
}

/* Tooltip */
.chart-tooltip {
  position: absolute;
  top: -65px;
  left: 50%;
  transform: translateX(-50%) translateY(0);
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease, transform 0.3s ease;
  z-index: 100;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.chart-tooltip::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid rgba(0, 0, 0, 0.9);
}

.chart-bar:hover .chart-tooltip {
  opacity: 1;
  transform: translateX(-50%) translateY(-5px);
}

.tooltip-value {
  display: block;
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 4px;
  color: #fff;
}

.tooltip-label {
  display: block;
  font-size: 12px;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Caso real */
.case-data {
  background: #f8fafc;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 30px;
  border: 2px solid #129bef;
  grid-column: span 1;
}

.case-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.case-title .material-symbols-outlined {
  font-size: 20px;
  color: #129bef;
}

.case-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.case-item {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.case-label {
  font-weight: 600;
  color: #666;
  min-width: 80px;
}

.case-value {
  color: #333;
  font-size: 15px;
}

.case-value.highlight {
  color: #129bef;
  font-weight: 700;
  font-size: 16px;
}

.case-item.savings {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
}

.savings-value {
  color: #16a34a;
  font-weight: 700;
  font-size: 18px;
}

.case-value.additional {
  font-style: italic;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

/* Métricas */
.metrics-section {
  background: linear-gradient(135deg, rgba(18, 155, 239, 0.05) 0%, rgba(74, 183, 255, 0.05) 100%);
  border-radius: 12px;
  padding: 24px;
  border: 1px solid rgba(18, 155, 239, 0.1);
  grid-column: span 1;
}

.metrics-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.metrics-title .material-symbols-outlined {
  font-size: 20px;
  color: #129bef;
}

.metrics-text {
  color: #666;
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
}

/* Responsive */
@media (max-width: 1024px) {
  .benefits-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }

  .benefit-card.expanded {
    grid-column: span 2;
  }

  .expanded-inner {
    grid-template-columns: 1fr;
  }

  .highlight-number,
  .chart-container,
  .case-data,
  .metrics-section {
    grid-column: span 1;
  }
}

@media (max-width: 768px) {
  .products-benefits-section {
    padding: 60px 15px;
  }

  .content-header {
    margin-bottom: 40px;
  }

  .benefits-title {
    font-size: 1.8rem;
  }

  .benefits-subtitle {
    font-size: 16px;
  }

  .benefits-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .benefit-card.expanded {
    grid-column: span 1;
  }

  .card-header {
    padding: 30px 20px;
  }

  .benefit-card.expanded .card-header {
    padding-bottom: 15px;
  }

  .expanded-inner {
    padding: 0 20px 30px 20px;
  }

  .benefit-icon {
    width: 60px;
    height: 60px;
    font-size: 1.8rem;
  }

  .benefit-title {
    font-size: 1.2rem;
  }

  .benefit-description {
    font-size: 14px;
  }

  .number-value {
    font-size: 3rem;
  }

  .chart-bar-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .chart-label {
    min-width: auto;
  }

  .chart-bar-container {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .products-benefits-section {
    padding: 40px 10px;
  }

  .card-header {
    padding: 25px 15px;
  }

  .expanded-inner {
    padding: 0 15px 25px 15px;
  }

  .benefit-icon {
    width: 55px;
    height: 55px;
    font-size: 1.6rem;
    margin-bottom: 20px;
  }

  .benefit-title {
    font-size: 1.1rem;
  }

  .benefit-description {
    font-size: 13px;
  }

  .number-value {
    font-size: 2.5rem;
  }

  .number-label {
    font-size: 1rem;
  }

  .case-data,
  .metrics-section {
    padding: 20px;
  }

  .chart-bar-container {
    height: 35px;
  }
}
</style>
